import React, { useEffect, useMemo, useRef } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useIdentity } from '@equipmentshare/auth0-react'
import { OAuthIntegrationCard } from '../oauth-integration-card/oauth-integration-card'
import { OAuthIntegrationAdditionalCard } from '../oauth-integration-additional-card/oauth-integration-additional-card'
import { useAvailableIntegrationQuery, useCreateFeedMutation, useFetchFeedOAuthUrlMutation, useSearchFeedsQuery } from '../../../graphql'
import { useModal } from '../../../providers'
import { TextInput } from '../../text-input/text-input'
import { isInIframe, trackAnalyticEvent, ANALYTICS_LOCATIONS, ACTIVE_FEEDS_QUERY_VARIABLES } from '../../../..'
import { DEFAULT_AEMPV2_INTEGRATION_DESCRIPTION, DEFAULT_INTEGRATION_DESCRIPTION, getUniqueFeedDescription } from '../../create-form/utils'
import { FeedDescriptionSection, FormInputSection, LoadingContainer, StyledBox, StyledIntegrationTypography } from './oauth-integration-create.styles'

export type OAuthIntegrationCreateProps = {
	integrationKey: string,
}

export const OAuthIntegrationCreate: React.FC<OAuthIntegrationCreateProps> = ( { integrationKey } ) => {
	const { companyName } = useIdentity()
	const { data: integrationData, isLoading: isLoadingIntegration } = useAvailableIntegrationQuery( { id: integrationKey } )
	const { data: feedsData, isLoading: isLoadingFeeds } = useSearchFeedsQuery( ACTIVE_FEEDS_QUERY_VARIABLES )

	const { mutateAsync: createFeed } = useCreateFeedMutation()
	const [ feedId, setFeedId ] = React.useState<string | null>( null )
	const { mutateAsync: fetchFeedOAuthUrl } = useFetchFeedOAuthUrlMutation()
	const [ feedDescription, setFeedDescription ] = React.useState<string>( '' )
	const [ feedDescriptionError, setFeedDescriptionError ] = React.useState<boolean>( false )
	const { showModal } = useModal()
	const [ isConnecting, setIsConnecting ] = React.useState( false )
	const isInAnIframe = useRef( isInIframe() ).current
	const existingFeedNames = useMemo( () => feedsData?.searchFeeds?.feeds?.map( ( feed ) => feed.name ) ?? [], [ feedsData ] )

	const onFeedDescriptionChange = ( value: string ) => {
		setFeedDescription( value )
		setFeedDescriptionError( value.length > 32 )
	}


	useEffect( () => {
		if ( !isLoadingFeeds && !feedDescription ) {
			const defaultDescription = integrationData?.integration?.isAEMPV2 ? DEFAULT_AEMPV2_INTEGRATION_DESCRIPTION : DEFAULT_INTEGRATION_DESCRIPTION
			const uniqueDescription = getUniqueFeedDescription( defaultDescription, existingFeedNames )
			onFeedDescriptionChange( uniqueDescription )
			setFeedDescriptionError( false )
		}
	}, [ isLoadingFeeds, integrationData?.integration?.isAEMPV2, existingFeedNames, feedDescription ] )


	if ( isLoadingIntegration || !integrationData?.integration ) {
		// show a mui spinner icon in the left
		return (
			<LoadingContainer>
				<CircularProgress />
			</LoadingContainer>
		)
	}

	const trackConnectToIntegrationClick = ( integrationName: string ) => {
		trackAnalyticEvent(
			ANALYTICS_LOCATIONS.CREATE_PAGE,
			'Click',
			'Connect to Integration',
			{
				integration: `${integrationName} (${integrationKey})`,
				...( companyName && { companyName } ),
			},
		)
	}

	const onConnectPress = async() => {
		// integrationData.integration is defined when this runs (guard above); optional chain for closure type safety
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- closure in async callback
		const integrationName = integrationData?.integration?.name ?? integrationKey
		setIsConnecting( true )

		if ( !feedId && integrationData.integration ) {
			setIsConnecting( true )
			const feed = await createFeed( { input: {
				integrationKey,
				name: feedDescription === '' ? getUniqueFeedDescription( integrationData.integration.isAEMPV2 ? DEFAULT_AEMPV2_INTEGRATION_DESCRIPTION : DEFAULT_INTEGRATION_DESCRIPTION, existingFeedNames ) : feedDescription,
				vendorId: integrationData.integration.vendorId,
			} } )
			if ( feed.createFeedMutation?.error ) {
				setIsConnecting( false )
				showModal( {
					title: 'Error Creating Feed',
					headerText: 'There was an error creating the feed',
					content: feed.createFeedMutation.error.message,
				} )
				trackAnalyticEvent(
					ANALYTICS_LOCATIONS.CREATE_PAGE,
					'Submission Error',
					feed.createFeedMutation.error.message || 'Unknown Error',
					{
						integration: `${integrationData.integration.name} (${integrationKey})`,
						...( companyName && { companyName } ),
					},
				)
				// Handle error accordingly, e.g., show a notification
			} else if ( feed.createFeedMutation?.feedId ) {
				setFeedId( feed.createFeedMutation.feedId )
				try {
					const oAuthURL = await fetchFeedOAuthUrl( { feedId: feed.createFeedMutation.feedId } )
					if ( !oAuthURL.feedOAuthURL ) {
						throw new Error( 'No OAuth URL returned from server.' )
					}
					trackAnalyticEvent(
						ANALYTICS_LOCATIONS.CREATE_PAGE,
						'Success',
						'OAuth Connect - Redirect',
						{
							integration: `${integrationData.integration.name} (${integrationKey})`,
							...( companyName && { companyName } ),
						},
					)
					// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
					if ( isInAnIframe ) {
						window.parent.postMessage( {
							type: 'navigate',
							target: '_self',
							href: oAuthURL.feedOAuthURL,
						}, '*' )
						return
					} else {
						window.location.href = oAuthURL.feedOAuthURL
						return
					}
				} catch ( error ) {
					showModal( {
						title: 'Error Fetching OAuth URL',
						headerText: 'There was a problem connecting to the integration',
						content: `Please try again later. Contact support if the issue persists. ${( error as Error ).message}`,
					} )
					trackAnalyticEvent(
						ANALYTICS_LOCATIONS.CREATE_PAGE,
						'Submission Error',
						( error as Error ).message || 'Unknown Error',
						{
							integration: `${integrationData.integration.name} (${integrationKey})`,
							...( companyName && { companyName } ),
						},
					)
				} finally {
					setIsConnecting( false )
				}
			}
			setIsConnecting( false )
		}

		trackConnectToIntegrationClick( integrationName )
	}

	const renderFeedDescriptionSection = () => {
		return (
			<FormInputSection spacing={2}>
				<FeedDescriptionSection spacing={2}>
					<Typography variant="h3" gutterBottom>
					Edit Feed Description (Optional)
					</Typography>
					<TextInput
						name="feedDescription"
						label="Feed Description"
						placeholder="Enter a description for this feed"
						value={feedDescription}
						onBlur={() => onFeedDescriptionChange( feedDescription.trim() )}
						onChange={ ( { target } ) => onFeedDescriptionChange( target.value ) }
						error={feedDescriptionError}
						errorText={feedDescriptionError ? 'Feed description cannot exceed 32 characters' : undefined}
					/>
				</FeedDescriptionSection>
			</FormInputSection>
		)
	}

	return (
		<Box>
			<StyledIntegrationTypography color="textSecondary" gutterBottom>
				To allow data sharing between {integrationData.integration.name} and T3, you&apos;ll need to log in to your account and grant access in your {integrationData.integration.name} settings.
			</StyledIntegrationTypography>
			<StyledBox>
				{renderFeedDescriptionSection()}
				<OAuthIntegrationCard
					isEditing={false}
					isFullyConnected={false}
					isPartiallyConnected={false}
					integrationName={integrationData.integration.name || ''}
					helpUrls={integrationData.integration.helpUrls}
					onPress={onConnectPress}
					buttonText={`Connect to ${integrationData.integration.name}`}
					isConnecting={isConnecting}
				/>
				{integrationData.integration.supportsPostAuth && <OAuthIntegrationAdditionalCard
					isAdditionalActionDisabled
					additionalUrl=""
					additionalUrlText={`Use the button below to visit ${integrationData.integration.name} settings, and make sure T3 has access within ${integrationData.integration.name} settings.`}
					integrationTitle={integrationData.integration.name || ''}
					buttonText="Enable T3 Access"
				/>}
			</StyledBox>
		</Box>
	)
}
